import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import Notification from 'rc-notification';
import { ThemeProps, RTLProps } from '@/shared/prop-types/ReducerProps';

// Notification instance
let welcomeNotification = null;
Notification.newInstance(
  {
    style: { top: 65 },
  },
  // eslint-disable-next-line
  n => (welcomeNotification = n),
);

// Enhanced BasicNotification with CSS animations
const EnhancedBasicNotification = ({ title, theme }) => {
  const isDark = theme.className === 'theme-dark';
  
  return (
    <div className={`enhanced-notification ${isDark ? 'dark-theme' : 'light-theme'}`}>
      <div className="notification-header">
        <div className="restaurant-icon">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M8.1 13.34L3.91 
            9.16C2.35 7.59 2.35 5.06 3.91 3.5L10.93 10.5L8.1 13.34Z"
              fill={isDark ? '#f5f5f5' : '#4527A0'}
            />
            <path
              d="M14.88 11.53L13.41 13L20.29 19.88C20.68 
            20.27 21.32 20.27 21.71 19.88C22.1 19.49 22.1 18.85 21.71 18.46L14.88 11.53Z"
              fill={isDark ? '#f5f5f5' : '#4527A0'}
            />
            <path
              d="M17.59 11.12L19.07 9.65C19.46 9.26 19.46 
            8.62 19.07 8.23L15.77 4.93C15.38 4.54 14.74 4.54 14.35 4.93L12.88 6.4L17.59 11.12Z"
              fill={isDark ? '#f5f5f5' : '#4527A0'}
            />
            <path
              d="M11.94 11.47C12.33 11.86 12.96 11.86 
            13.35 11.47L14.83 9.99L13.36 8.52L11.88 9.99C11.5 10.38 11.5 11.02 11.88 11.41L11.94 11.47Z"
              fill={isDark ? '#f5f5f5' : '#4527A0'}
            />
          </svg>
        </div>
        <h4 className="notification-title">
          Welcome to Bitevise-Agent
        </h4>
      </div>
      <div className="progress-bar">
        <div className="progress-bar-inner" />
      </div>
      <div className="notification-content">
        <p>Your culinary journey begins now! Explore our dashboard to manage your Agent operations efficiently.</p>
      </div>
    </div>
  );
};

EnhancedBasicNotification.propTypes = {
  title: PropTypes.string.isRequired,
  theme: ThemeProps.isRequired,
};

const WelcomeNotification = (theme, rtl, setIsNotificationShown, isNotificationShown) => {
  // const title = 'Welcome to Bitevise-Agent';

  const initialProps = {
    content: (
      <EnhancedBasicNotification
        // title={title}
        theme={theme}
      />
    ),
    key: 'welcome-notification',
    closable: true,
    duration: 2,
    style: {
      top: 20,
      left: 'calc(100vw - 100%)',
      maxWidth: '400px',
    },
    className: `right-up ${rtl.direction}-support enhanced-notification-wrapper`,
    onClose() {
      setIsNotificationShown(true);
    },
  };

  if (!isNotificationShown) {
    // Add CSS once to the document
    if (!document.getElementById('enhanced-notification-styles')) {
      const styleTag = document.createElement('style');
      styleTag.id = 'enhanced-notification-styles';
      styleTag.innerHTML = `
        .enhanced-notification-wrapper {
          animation: slide-in 0.5s ease-out;
          border-radius: 8px;
          overflow: hidden;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
        }
        
        @keyframes slide-in {
          from {
            transform: translateY(-20px);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
        
        .enhanced-notification {
          padding: 16px 20px;
          border-radius: 8px;
          font-family: 'Roboto', sans-serif;
          animation: fade-in 0.5s ease-out;
        }
        
        @keyframes fade-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        
        .enhanced-notification.light-theme {
          background: linear-gradient(135deg, #ffffff, #f7f7f7);
          color: #333;
        }
        
        .enhanced-notification.dark-theme {
          background: linear-gradient(135deg, #2c2c2c, #1a1a1a);
          color: #f5f5f5;
        }
        
        .notification-header {
          display: flex;
          align-items: center;
          margin-bottom: 10px;
        }
        
        .restaurant-icon {
          margin-right: 12px;
          animation: rotate-in 0.8s ease-out;
        }
        
        @keyframes rotate-in {
          from {
            transform: rotate(-45deg);
            opacity: 0;
          }
          to {
            transform: rotate(0);
            opacity: 1;
          }
        }
        
        .notification-title {
          margin: 0;
          font-size: 18px;
          font-weight: 600;
          letter-spacing: 0.3px;
          animation: slide-in-right 0.6s ease-out;
        }
        
        @keyframes slide-in-right {
          from {
            transform: translateX(20px);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        
        .notification-content p {
          margin: 10px 0 5px;
          font-size: 14px;
          line-height: 1.5;
          opacity: 0.85;
          animation: fade-in 0.8s ease-out;
        }
        
        .progress-bar {
          height: 3px;
          background: rgba(0, 0, 0, 0.1);
          margin: 5px 0;
          border-radius: 2px;
          overflow: hidden;
        }
        
        .dark-theme .progress-bar {
          background: rgba(255, 255, 255, 0.1);
        }
        
        .progress-bar-inner {
          height: 100%;
          width: 100%;
          background: linear-gradient(90deg, #4527A0, #7B1FA2);
          animation: progress 5s linear;
          transform-origin: left;
        }
        
        @keyframes progress {
          from { transform: scaleX(0); }
          to { transform: scaleX(1); }
        }
        
        .dark-theme .progress-bar-inner {
          background: linear-gradient(90deg, #7B1FA2, #4527A0);
        }
      `;
      document.head.appendChild(styleTag);
    }
    
    welcomeNotification.notice(initialProps);
  }
};

WelcomeNotification.propTypes = {
  theme: ThemeProps.isRequired,
  rtl: RTLProps.isRequired,
  setIsNotificationShown: PropTypes.func.isRequired,
  isNotificationShown: PropTypes.bool,
};

WelcomeNotification.defaultProps = {
  isNotificationShown: false,
};

export default WelcomeNotification;
